import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {IRoleSubCalendar, ISubCalendar} from "../../interfaces/ISubCalendar";
import {customFetcher} from "../useFetch";
import {ICalendarAPIEvent} from "../../interfaces/ICalendarAPIEvent";

export const useGetSubCalendar = () => {
    const {data: subCalendarArray, isLoading: isLoadingSubCalendarArray, refetch: refetchSubCalendars} = useQuery<ISubCalendar[]>({
        refetchOnWindowFocus: false,
        queryKey: ["subcalendar"],
        queryFn: () => customFetcher({endpoint: "/calendar/sub_calendar/"}),
        initialData: [],
    });
    return {
        subCalendarArray: subCalendarArray as ISubCalendar[],
        isLoadingSubCalendarArray: isLoadingSubCalendarArray,
        refetchSubCalendars: refetchSubCalendars
    };
};

export const useUpdateSubCalendar = () => {
    const queryClient = useQueryClient();
    const {mutateAsync: updateSubCalendar, isPending: isUpdatingSubCalendar} = useMutation<
        ISubCalendar,
        Error,
        ISubCalendar
    >({
        mutationFn: (updatedSubCalendar: ISubCalendar) =>
            customFetcher({
                endpoint: `/calendar/sub_calendar/${updatedSubCalendar.id}/`,
                method: "PUT",
                body: updatedSubCalendar,
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: ["subcalendar"]});
        },
    });
    return {updateSubCalendar: updateSubCalendar, isUpdatingSubCalendar: isUpdatingSubCalendar};
};

export const useDeleteSubCalendar = () => {
    const queryClient = useQueryClient();
    const {mutateAsync: deleteSubCalendar, isPending: isDeletingSubCalendar} = useMutation({
        mutationFn: (idSubCalendar: number) =>
            customFetcher({
                endpoint: `/calendar/sub_calendar/${idSubCalendar}/`,
                method: "DELETE",
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: ["subcalendar"]});
        },
    });
    return {deleteSubCalendar: deleteSubCalendar, isDeletingSubCalendar: isDeletingSubCalendar};
};

export const useCreateSubCalendar = () => {
    const queryClient = useQueryClient();
    const {mutateAsync: createSubCalendar, isPending: isCreatingSubCalendar} = useMutation<ISubCalendar, Error, ISubCalendar>({
        mutationFn: (newSubCalendar: ISubCalendar) =>
            customFetcher({
                endpoint: `/calendar/sub_calendar/`,
                method: "POST",
                body: newSubCalendar,
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: ["subcalendar"]});
        },
    });
    return {createSubCalendar, isCreatingSubCalendar: isCreatingSubCalendar};
};

export const useGetCalendarEvents = () => {
    const {data, isLoading, error} = useQuery<ICalendarAPIEvent[]>({
        queryKey: ["calendarEvents"],
        queryFn: () => customFetcher({endpoint: "/calendar/events/"}),
        refetchOnWindowFocus: false,
        initialData: [],
    });
    return {events: data, isLoading, error};
};

export const useGetCalendarEvent = (id: string | null) => {
    const {data, isLoading, error, refetch} = useQuery<ICalendarAPIEvent>({
        queryKey: ["calendarEvent", id],
        queryFn: () => customFetcher({endpoint: `/calendar/events/${id}/`}),
        refetchOnWindowFocus: false,
        enabled: Boolean(id),
    });
    return {event: data, isLoading, error, refetch};
};

export const useGetCalendarEventsBTermin = () => {
    const {data, isLoading, error} = useQuery<ICalendarAPIEvent[]>({
        queryKey: ["calendarEvents"],
        queryFn: () => customFetcher({endpoint: "/calendar/events/b-termin/"}),
        refetchOnWindowFocus: false,
        initialData: [],
    });
    return {bevents: data, isLoading, error};
};

export const useCreateCalendarEvent = () => {
    const queryClient = useQueryClient();
    const {mutateAsync: createCalendarEvent, isPending: isCreatingCalendarEvent} = useMutation<
        ICalendarAPIEvent,
        Error,
        any
    >({
        mutationFn: (payload: any) =>
            customFetcher({
                endpoint: `/calendar/events/create/`,
                method: "POST",
                body: payload,
            }),

        onSuccess: async (res) => {
            await queryClient.invalidateQueries({queryKey: ["calendarEvents"]});

            const newId = res.id;
            await queryClient.invalidateQueries({queryKey: ["calendarEvent", newId]});

        },
    });
    return {createCalendarEvent, isCreatingCalendarEvent};
};

export const useUpdateCalendarEvent = () => {
    const queryClient = useQueryClient();
    const {mutateAsync: updateCalendarEvent, isPending: isUpdatingCalendarEvent} = useMutation<
        ICalendarAPIEvent,
        Error,
        { id: number; payload: any }
    >({
        mutationFn: ({id, payload}) =>
            customFetcher({
                endpoint: `/calendar/events/${id}/`,
                method: "PUT",
                body: payload,
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: ["calendarEvents"]});
        },
    });
    return {updateCalendarEvent, isUpdatingCalendarEvent};
};

export const useDeleteCalendarEvent = () => {
    const queryClient = useQueryClient();
    const {mutateAsync: deleteCalendarEvent, isPending: isDeletingCalendarEvent} = useMutation({
        mutationFn: (id: number) =>
            customFetcher({
                endpoint: `/calendar/events/${id}/`,
                method: "DELETE",
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: ["calendarEvents"]});
        },
    });
    return {deleteCalendarEvent, isDeletingCalendarEvent};
};


export const useGetRoleSubCalendar = () => {
    const {data, isLoading, error, refetch: refetchRoleSubCalendars} = useQuery<IRoleSubCalendar[]>({
        queryKey: ["roleSubCalendar"],
        queryFn: () => customFetcher({endpoint: "/calendar/role_sub_calendar/"}),
        refetchOnWindowFocus: false,
        initialData: [],
    });
    return {
        roleSubCalendarArray: data as IRoleSubCalendar[],
        isLoadingRoleSubCalendar: isLoading,
        errorRoleSubCalendar: error,
        refetchRoleSubCalendars: refetchRoleSubCalendars
    };
};

export const useCreateRoleSubCalendar = () => {
    const queryClient = useQueryClient();
    const {mutateAsync: createRoleSubCalendar, isPending: isCreatingRoleSubCalendar} =
        useMutation<IRoleSubCalendar, Error, Omit<IRoleSubCalendar, "id">>({
            mutationFn: (newRoleSubCal) =>
                customFetcher({
                    endpoint: "/calendar/role_sub_calendar/",
                    method: "POST",
                    body: newRoleSubCal,
                }),
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: ["roleSubCalendar"]});
            },
        });

    return {createRoleSubCalendar, isCreatingRoleSubCalendar};
};

export const useUpdateRoleSubCalendar = () => {
    const queryClient = useQueryClient();
    const {mutateAsync: updateRoleSubCalendar, isPending: isUpdatingRoleSubCalendar} =
        useMutation<IRoleSubCalendar, Error, IRoleSubCalendar>({
            mutationFn: (payload) =>
                customFetcher({
                    endpoint: `/calendar/role_sub_calendar/${payload.id}/`,
                    method: "PUT",
                    body: payload,
                }),
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: ["roleSubCalendar"]});
            },
        });

    return {updateRoleSubCalendar, isUpdatingRoleSubCalendar};
};

export const useDeleteRoleSubCalendar = () => {
    const queryClient = useQueryClient();
    const {mutateAsync: deleteRoleSubCalendar, isPending: isDeletingRoleSubCalendar} =
        useMutation<void, Error, number>({
            mutationFn: (id) =>
                customFetcher({
                    endpoint: `/calendar/role_sub_calendar/${id}/`,
                    method: "DELETE",
                }),
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: ["roleSubCalendar"]});
            },
        });

    return {deleteRoleSubCalendar, isDeletingRoleSubCalendar};
};
